/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';
import { i18n } from '@/packages/i18n';

import useLocalStorage from '@/composables/storage/useLocalStorage.js';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';

import useIdentityQueueEndpoints from '@/composables/api/useIdentityQueueEndpoints';
import useIdentityQueueHelpers from './useIdentityQueueHelpers';

const { getPendingIdentityDocuments } = useIdentityQueueEndpoints();
const { checkLocalStorage, bindLocalStorage } = useLocalStorage();
const { organizeRoutes, sortRoutes } = useIdentityQueueHelpers;

const { t } = i18n.global;
const EXCEPTION_BASED_ROUTES = ['BANXA.WALLET_OWNERSHIP'];
// map centry names to app routes
const IDENTITY_MAPPING_ROUTES = {
  BANXA: {
    PERSONAL_DETAILS: {
      name: t('IdentityQueueStore.name.upNextBillingDetails'),
      route: '/identity/identityConfirmation',
    },
    POA: {
      name: t('IdentityQueueStore.name.upNextProofOfAddress'),
      route: '/identity/proofOfAddress',
    },
    SOURCE_FUNDS: {
      name: t('IdentityQueueStore.name.upNextSof'),
      route: '/identity/sourceOfFunds',
    },
    PURPOSE_OF_TX: {
      name: t('IdentityQueueStore.name.upNextPurposeOfTransaction'),
      route: '/identity/purposeOfTransaction',
    },
    SOF_DOCUMENT: {
      name: t('IdentityQueueStore.name.upNextSofDocUpload'),
      route: '/identity/sofDocumentUpload',
    },
    DOCUMENT: {
      name: t('IdentityQueueStore.name.upNextDocUpload'),
      route: '/identity/idUpload/nationalDriversLicence',
    },
    DOCUMENT_NUMBER: {
      name: t('IdentityQueueStore.name.upNextDocNumber'),
      route: '/identity/documentNumber',
    },
    SELFIE: {
      name: t('IdentityQueueStore.name.upNextSelfieDocUpload'),
      route: '/identity/selfie',
    },
    OCCUPATION: {
      name: t('IdentityQueueStore.name.upNextOccupation'),
      route: '/identity/occupation',
    },
    PEP_SOURCE_FUNDS: {
      name: t('IdentityQueueStore.name.upNextPepSof'),
      route: '/identity/pepSourceOfFunds',
    },
    PEP_SOF_DOCUMENT: {
      name: t('IdentityQueueStore.name.upNextPepSofDoc'),
      route: '/identity/pepSofDocumentUpload',
    },
    US_TAX_ID: {
      name: t('IdentityQueueStore.name.upNextSSN'),
      route: '/identity/ssn',
    },
    DLOCAL_CPF: {
      name: t('IdentityQueueStore.name.upNextDlocalCpf'),
      route: '/identity/dlocal/cpf',
    },
    DLOCAL_SAID: {
      name: t('IdentityQueueStore.name.upNextDlocalSaid'),
      route: '/identity/dlocal/said',
    },
    DLOCAL_CURP: {
      name: t('IdentityQueueStore.name.upNextDlocalCurp'),
      route: '/identity/dlocal/curp',
    },
    DLOCAL_DNI: {
      name: t('IdentityQueueStore.name.upNextDlocalDni'),
      route: '/identity/dlocal/dni',
    },
    DLOCAL_CC: {
      name: t('IdentityQueueStore.name.upNextDlocalCc'),
      route: '/identity/dlocal/cc',
    },
    DLOCAL_CI: {
      name: t('IdentityQueueStore.name.upNextDlocalCi'),
      route: '/identity/dlocal/ci',
    },
    DLOCAL_TIC: {
      name: t('IdentityQueueStore.name.upNextDlocalTic'),
      route: '/identity/dlocal/tic',
    },
    WALLET_OWNERSHIP: {
      name: t('IdentityQueueStore.name.upNextWalletOwnership'),
      route: '/identity/walletOwnership',
    },
  },
  SUMSUB: {
    PERSONAL_DETAILS: {
      name: t('IdentityQueueStore.name.upNextBillingDetails'),
      route: '/identity/identityConfirmation',
    },
    POA: {
      name: t('IdentityQueueStore.name.upNextProofOfAddress'),
      route: '/identity/sumsub',
    },
    SOURCE_FUNDS: {
      name: t('IdentityQueueStore.name.upNextSof'),
      route: '/identity/sumsub',
    },
    PURPOSE_OF_TX: {
      name: t('IdentityQueueStore.name.upNextPurposeOfTransaction'),
      route: '/identity/sumsub',
    },
    SOF_DOCUMENT: {
      name: t('IdentityQueueStore.name.upNextSofDocUpload'),
      route: '/identity/sumsub',
    },
    DOCUMENT: {
      name: t('IdentityQueueStore.name.upNextDocUpload'),
      route: '/identity/sumsub',
    },
    SELFIE: {
      name: t('IdentityQueueStore.name.upNextSelfieDocUpload'),
      route: '/identity/sumsub',
    },
  },
};

export const useIdentityQueueStore = defineStore({
  id: 'identityQueue',
  state: () => ({
    identityRoutes: checkLocalStorage('identityRoutes', []),
    queueCounter: checkLocalStorage('queueCounter', 1),
    totalSteps: checkLocalStorage('totalSteps', 1),
    sumsubRecollection: checkLocalStorage('sumsubRecollection', false),
    submittingToSumsub: false,
  }),
  actions: {
    async fetchIdentityDocuments(fromPath) {
      const { data } = await getPendingIdentityDocuments();
      const overrideRoutes = [];

      return new Promise((res, rej) => {
        if (!data) {
          return;
        }
        Object.entries(data).forEach(([key, value]) => {
          organizeRoutes(
            this,
            key,
            value,
            fromPath,
            IDENTITY_MAPPING_ROUTES,
            // EXCEPTION_BASED_ROUTES,
            overrideRoutes,
          );
        });

        // const hasExceptionBased = this.identityRoutes.some((route) => EXCEPTION_BASED_ROUTES.map((routeName) => {
        //     const [provider, name] = routeName.split('.');
        //     return IDENTITY_MAPPING_ROUTES[provider.toUpperCase()][
        //       name.toUpperCase()
        //     ].route;
        //   }).some((exceptionRoute) => exceptionRoute === route.url));

        const hasDLocalException = this.identityRoutes.some((route) => route.url.toLowerCase().includes('dlocal'));

        // 1st condition: from personal details and non identity pages (like buy/sell) we can have the queue overridden by new kyc_trigger requirements by the user's selected country
        // 2nd condition: from non identity pages, we can call the queue and it should override the queue array
        // 3rd condition: from sumsub pages we can have the queue overridden by sumsub recollection requirement

        if (
          fromPath === '/identity/identityConfirmation'
          || (fromPath && !fromPath.includes('identity')) // && !hasExceptionBased
          || this.sumsubRecollection
          || hasDLocalException
        ) {
          this.identityRoutes = overrideRoutes;
          this.resetCounter();
        }

        this.identityRoutes = sortRoutes(this.identityRoutes);
        this.calculateTotalSteps();
        bindLocalStorage('identityRoutes', this.identityRoutes);
        res();
      });
    },
    calculateTotalSteps() {
      const numberOfBanxaSteps = this.identityRoutes.reduce((acc, identity) => {
        if (identity.provider.toUpperCase() === 'BANXA') {
          acc += 1;
        }
        return acc;
      }, 0);

      // we're count sumsub items as 1 as all documents are collected in one page
      const numberOfSumsubSteps = this.identityRoutes.some(
        (identity) => identity.provider.toUpperCase() === 'SUMSUB',
      )
        ? 1
        : 0;

      const totalSteps = numberOfBanxaSteps + numberOfSumsubSteps;

      if (totalSteps === 0) {
        this.totalSteps = 1;
        return;
      }

      this.totalSteps = numberOfBanxaSteps + numberOfSumsubSteps;
      bindLocalStorage('totalSteps', this.totalSteps);
    },
    updateToNextRoute(route = null) {
      // There might be occucation where we receive two payloads from
      // sumsub. In that case, for the second payload, we have to make
      // sure we don't update the queue counter.
      if (this.submittingToSumsub && route?.name.includes('sumsub')) {
        return;
      }
      this.queueCounter += 1;
      bindLocalStorage('queueCounter', this.queueCounter);
    },
    updateToPreviousRoute() {
      this.queueCounter -= 1;
      bindLocalStorage('queueCounter', this.queueCounter);
    },
    resetCounter() {
      this.queueCounter = 1;
      bindLocalStorage('queueCounter', this.queueCounter);
    },
    resetSteps() {
      this.totalSteps = 1;
      bindLocalStorage('totalSteps', this.totalSteps);
    },
    async fetchNextIdentityRoute(route) {
      const orderStore = useOrderFormStore();
      const defaultPaymentRoute = `/payment/${orderStore.getActivePaymentByMode}`;

      return this.fetchIdentityDocuments(route?.path).then(() => {
        // on sumsub recollection we route straight to /status on SumsubForm
        if (route?.name.includes('sumsub') && this.sumsubRecollection) {
          return false;
        }

        if (
          this.identityRoutes.length === 0
          || (orderStore.getActivePaymentByMode
            && this.queueCounter > this.totalSteps)
        ) {
          return defaultPaymentRoute;
        }

        return this.identityRoutes[this.queueCounter - 1].url;
      });
    },
    toggleSumsubRecollection(isRequired) {
      this.sumsubRecollection = isRequired;
      bindLocalStorage('sumsubRecollection', this.sumsubRecollection);
    },
  },
});
